import { inject, Injectable, PLATFORM_ID, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { HttpService } from '../data-access/http/http.service';
import { isBrowser } from '../utils/platform.util';
import { decodeToken, isTokenValid as validateToken, extractPermissionMask } from '../utils/jwt.util';
import {
  BehaviorSubject,
  Subject,
  Observable,
  of,
  tap,
  catchError,
  filter,
  finalize,
  map,
  switchMap,
} from 'rxjs';
import { ChunkedTokenStorage } from '../utils/helpers/chunked-token-storage';
import { HttpHeaders } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { PermissionService } from '../permissions/permission.service';
import { UserProfileData, LoginResponse, JwtPayload } from './auth.models';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly chunkedStorage = inject(ChunkedTokenStorage);
  private readonly http = inject(HttpService);
  private readonly router = inject(Router);
  private readonly cookieService = inject(CookieService);
  private readonly permissionService = inject(PermissionService);

  // --- Auth State (Signals) ---
  private readonly _isAuthenticated = signal<boolean>(false);
  private readonly _userProfile = signal<UserProfileData | null>(null);

  readonly isAuthenticated = this._isAuthenticated.asReadonly();
  readonly userProfile = this._userProfile.asReadonly();

  readonly userId = computed(() => this._userProfile()?.userId ?? null);
  readonly userEmail = computed(() => this._userProfile()?.email ?? null);
  readonly userName = computed(() => {
    const profile = this._userProfile();
    return profile?.fullNameAr || profile?.fullNameEn || profile?.username || null;
  });

  readonly mustChangePassword = computed(() => this._userProfile()?.mustChangePassword ?? false);

  // --- Event Emitters (Legacy Compatibility) ---
  private readonly loginSuccessSubject = new Subject<void>();
  readonly loginSuccess$ = this.loginSuccessSubject.asObservable();

  private readonly logoutSubject = new Subject<void>();
  readonly logout$ = this.logoutSubject.asObservable();

  // --- Observables for Legacy Consumers ---
  private readonly authStateSubject = new BehaviorSubject<boolean>(false);
  readonly authState$ = this.authStateSubject.asObservable();

  private readonly userProfileSubject = new BehaviorSubject<UserProfileData | null>(null);
  readonly userProfile$ = this.userProfileSubject.asObservable();

  // --- Token Refresh Management ---
  private refreshTokenInProgress = false;
  private refreshTokenSubject = new BehaviorSubject<boolean | null>(null);

  constructor() {
    this.initializeAuth();
  }

  /**
   * Handle login response and store tokens
   */
  handleLogin(loginResponse: LoginResponse): Observable<boolean> {
    if (!isBrowser(this.platformId) || !loginResponse) return of(false);

    try {
      const accessToken = loginResponse.accessToken || loginResponse.value;
      if (!accessToken) return of(false);

      // Store tokens
      const accessTokenStored = this.chunkedStorage.storeToken(accessToken, 'accessToken');
      const refreshTokenStored = this.storeRefreshToken(loginResponse.refreshToken);

      if (!accessTokenStored || !refreshTokenStored) return of(false);

      const decodedToken = decodeToken<JwtPayload>(accessToken);
      if (!decodedToken) return of(false);

      const userId = decodedToken.userId || decodedToken.sub;
      if (!userId) return of(false);

      this.storeUserId(userId);
      this.storeSession(decodedToken, loginResponse);

      this.loginSuccessSubject.next();
      return of(true);
    } catch (error) {
      return of(false);
    }
  }

  /**
   * Logout user
   */
  logout(): void {
    this.clearSession();
    this.router.navigate(['/login']);
  }

  /**
   * Refresh access token
   */
  refreshToken(): Observable<boolean> {
    if (!isBrowser(this.platformId)) return of(false);

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return of(false);

    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.asObservable().pipe(
        filter((result): result is boolean => result !== null)
      );
    }

    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);

    return this.http.fetchPost('sso', 'auth/refresh-token', undefined, { refreshToken }, {
      headers: new HttpHeaders({ 'PreferredLanguage': 'ar', 'Accept-Language': 'ar' })
    }).pipe(
      map((res: any) => res.body?.value),
      switchMap((value: any) => {
        if (value?.accessToken) {
          this.chunkedStorage.storeToken(value.accessToken, 'accessToken');
          if (value.refreshToken) this.storeRefreshToken(value.refreshToken);
          
          const decoded = decodeToken<JwtPayload>(value.accessToken);
          this.permissionService.loadPermissions(extractPermissionMask(decoded));
          
          this.refreshTokenSubject.next(true);
          return of(true);
        }
        throw new Error('Invalid refresh token response');
      }),
      catchError((err) => {
        this.clearSession();
        this.refreshTokenSubject.next(false);
        throw err;
      }),
      finalize(() => {
        this.refreshTokenInProgress = false;
        this.refreshTokenSubject.next(null);
      })
    );
  }

  /**
   * Check if token is expiring soon
   */
  isTokenExpiringSoon(bufferTimeMs: number = 5 * 60 * 1000): boolean {
    if (!isBrowser(this.platformId)) return false;
    try {
      const token = this.chunkedStorage.getToken('accessToken');
      if (!token) return false;
      const decoded = decodeToken<JwtPayload>(token);
      return decoded?.exp ? (Date.now() + bufferTimeMs >= decoded.exp * 1000) : false;
    } catch {
      return false;
    }
  }

  /**
   * Initial auth check on load
   */
  private initializeAuth(): void {
    if (!isBrowser(this.platformId)) return;

    const token = this.chunkedStorage.getToken('accessToken');
    if (token && validateToken(token)) {
      const decoded = decodeToken<JwtPayload>(token);
      this.storeSession(decoded);
    } else {
      this._isAuthenticated.set(false);
      this.authStateSubject.next(false);
    }
  }

  /**
   * Update all state and cookies from token/profile
   */
  private storeSession(decoded: JwtPayload, loginResponse?: LoginResponse): void {
    const mustChangePassword = !!(
      decoded.MustChangePasswordOnNextLogin === 'true' ||
      decoded.MustChangePasswordOnNextLogin === true ||
      decoded.mustChangePassword === 'true' ||
      decoded.mustChangePassword === true
    );

    const profile: UserProfileData = {
      username: decoded.fullNameAr || decoded.fullNameEn || decoded.name || decoded.username || '',
      email: decoded.email || loginResponse?.user?.email || '',
      userId: (decoded.userId || decoded.sub || '').toString(),
      fullNameAr: decoded.fullNameAr || loginResponse?.user?.fullNameAr || '',
      fullNameEn: decoded.fullNameEn || loginResponse?.user?.fullNameEn || '',
      role: decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || '',
      profileImage: decoded.profileImage || loginResponse?.user?.profilePicture || 'images/Avatar.png',
      mustChangePassword
    };

    // Update state
    this._isAuthenticated.set(true);
    this._userProfile.set(profile);
    this.authStateSubject.next(true);
    this.userProfileSubject.next(profile);

    // Persist email and permissions
    if (profile.email) {
      this.cookieService.set('apa-client-email', profile.email, this.getExpiryDate(7), '/', undefined, false, 'Lax');
    }
    this.cookieService.set('apa-must-change-password', mustChangePassword.toString(), this.getExpiryDate(7), '/', undefined, false, 'Lax');
    this.permissionService.loadPermissions(extractPermissionMask(decoded));

    // Async sync with server if needed
    if (!mustChangePassword && profile.userId) {
      this.syncProfileFromServer();
    }
  }

  private syncProfileFromServer(): void {
    this.http.get('sso', 'user-management/users/my-profile').pipe(
      tap((res: any) => {
        if (res?.value) {
          const current = this._userProfile();
          const updated: UserProfileData = {
            ...current!,
            profileImage: res.value.profilePicture || current?.profileImage || 'images/Avatar.png',
            email: res.value.email || current?.email,
            fullNameAr: res.value.fullNameAr || current?.fullNameAr,
            fullNameEn: res.value.fullNameEn || current?.fullNameEn,
            username: res.value.fullNameAr || res.value.fullNameEn || current?.username,
            role: res.value.role || current?.role,
          };
          this._userProfile.set(updated);
          this.userProfileSubject.next(updated);
          if (updated.email) {
            this.cookieService.set('apa-client-email', updated.email, this.getExpiryDate(7), '/', undefined, false, 'Lax');
          }
        }
      }),
      catchError(() => of(null))
    ).subscribe();
  }

  private clearSession(): void {
    if (!isBrowser(this.platformId)) return;
    this.chunkedStorage.clearToken('accessToken');
    this.cookieService.delete('apa-client-refreshToken', '/');
    this.cookieService.delete('apa-client-userId', '/');
    this.cookieService.delete('apa-client-email', '/');
    this.cookieService.delete('apa-must-change-password', '/');
    this.permissionService.clearPermissions();
    this._isAuthenticated.set(false);
    this._userProfile.set(null);
    this.authStateSubject.next(false);
    this.userProfileSubject.next(null);
    this.logoutSubject.next();
  }

  private storeRefreshToken(token: string): boolean {
    if (!token) return false;
    this.cookieService.set('apa-client-refreshToken', token, this.getExpiryDate(7), '/', undefined, false, 'Lax');
    return true;
  }

  private getRefreshToken(): string | null {
    return this.cookieService.get('apa-client-refreshToken') || null;
  }

  private storeUserId(id: string): void {
    this.cookieService.set('apa-client-userId', id, this.getExpiryDate(7), '/', undefined, false, 'Lax');
  }

  private getExpiryDate(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  }

  // Legacy Compatibility Helper
  getAuthState(): Observable<boolean> {
    return this.authState$;
  }
}
